import type { ReactNode } from 'react';

/**
 * FixedActionBar — pins a primary action button to the bottom of the viewport so
 * it is ALWAYS visible, even when the role card / content overflows and scrolls.
 *
 * Safe-area aware (env(safe-area-inset-bottom)). On the host, it sits ABOVE the
 * RingmasterBar: pass `offsetBottom` (the RingmasterBar height, ~96px) so the two
 * never collide — the same offset pattern the NarrationBanner uses.
 *
 * The stack, bottom → top on the host: RingmasterBar → FixedActionBar → content.
 */
export function FixedActionBar({
  children,
  offsetBottom = 0,
}: {
  children: ReactNode;
  offsetBottom?: number;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: offsetBottom,
        zIndex: 60,
        display: 'flex',
        justifyContent: 'center',
        padding: 'var(--space-4) var(--space-5)',
        paddingBottom: offsetBottom
          ? 'var(--space-3)'
          : 'max(var(--space-4), env(safe-area-inset-bottom, 0px) + var(--space-3))',
        // A bottom-LEFT corner control (One Night's roles button) reserves room
        // here so the centered action column never runs underneath it. Zero
        // unless something sets --rm-corner-inset, so every other surface is
        // byte-identical to before.
        paddingLeft: 'calc(var(--space-5) + var(--rm-corner-inset, 0px))',
        // Fade the content out beneath the button so it reads as a floating bar.
        // Surface is the lifted ink tone (was flat --bg-base) for a subtle plinth.
        background:
          'linear-gradient(0deg, var(--ink-2, #150d0f) 0%, var(--ink-2, #150d0f) 55%, rgba(21,13,15,0) 100%)',
        pointerEvents: 'none',
      }}
    >
      {/* Gold top hairline — reads as the edge of a lit stage apron. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background:
            'linear-gradient(90deg, rgba(230,184,102,0) 0%, rgba(230,184,102,0.55) 50%, rgba(230,184,102,0) 100%)',
          pointerEvents: 'none',
        }}
      />
      <div style={{ width: '100%', maxWidth: 360, pointerEvents: 'auto' }}>{children}</div>
    </div>
  );
}

/**
 * Shared SECONDARY styling for the fixed action bar — the same pill geometry as
 * `fixedPrimaryButtonStyle` with `Button variant="secondary"`'s skin, so a bar
 * can demote its own action without inventing a third look.
 *
 * Exists because exactly one control per surface may read as primary (issue
 * #29): below the player floor the invite is the useful action, so the curtain
 * button steps down to this rather than sitting there as a dimmed primary.
 */
export const fixedSecondaryButtonStyle = {
  width: '100%',
  fontFamily: 'var(--font-ui)',
  fontWeight: 600,
  fontSize: 16,
  borderRadius: 'var(--radius-pill)',
  padding: '16px 24px',
  background: 'var(--btn-secondary-bg)',
  color: 'var(--btn-secondary-text)',
  border: '1px solid var(--btn-secondary-stroke)',
  cursor: 'pointer',
  minHeight: 52,
  transition: 'all var(--duration-base) var(--ease-standard)',
} as const;

/** Shared primary-button styling for the fixed action bar (white pill). */
export const fixedPrimaryButtonStyle = {
  width: '100%',
  fontFamily: 'var(--font-ui)',
  fontWeight: 600,
  fontSize: 16,
  borderRadius: 'var(--radius-pill)',
  padding: '16px 24px',
  background: 'var(--btn-primary-bg)',
  color: 'var(--btn-primary-text)',
  border: 'none',
  cursor: 'pointer',
  minHeight: 52,
  transition: 'all var(--duration-base) var(--ease-standard)',
} as const;
